import clsx from "clsx";
import React, { useMemo, useState, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import Paper from "components/common/Paper";
import SpaceDetailsHeader from "./SpaceDetailsHeader";
import SpaceEmptyContent from "./SpaceEmptyContent";
import TabsContainer from "components/common/TabsContainer";
import SpaceDetailsMessageTab from "./SpaceDetailsMessageTab";
import SpaceDetailsPinnedSkuTab from "./SpaceDetailsPinnedSkuTab";
import SpaceDetailsForecastTab from "./SpaceDetailsForecastTab";
import SpaceDetailsFileTab from "./SpaceDetailsFileTab";
import SpaceDetailsNoteTab from "./SpaceNotes.tsx";
import useToggle from "hooks/useToggle";
import SpaceDetailsPinnedMessagesSidebar from "./SpaceDetailsPinnedMessagesSidebar";
import SpaceDetailsMembersSidebar from "./SpaceDetailsMembersSidebar";
import { useGetSingleSpace } from "hooks/spaceHooks";
import useGetUserProfile from "hooks/useGetUserProfile";
import { useAppSelector } from "hooks/storeHooks";
import LoadingContent from "components/common/LoadingContent";
import SpaceMessageLoadingState from "./SpaceMessage/SpaceMessageLoadingState";
import SpaceDetailsTaskTab from "./SpaceTask/SpaceDetailsTaskTab";

type SpaceDetailsProps = {
    expandConversation: boolean;
    handleToggleExpandConversation: () => void;
    isGuest: boolean;
};

export default function SpaceDetails(props: SpaceDetailsProps) {
    const { expandConversation, handleToggleExpandConversation, isGuest } = props;
    const { spaceId } = useParams();

    const [activeTab, setActiveTab] = useState(0);

    const [openPinnedSideBar, togglePinnedSideBar, setOpenPinnedSideBar] = useToggle();
    const [openMembersSidebar, toggleMembersSidebar, setOpenMembersSidebar] = useToggle();
    const [pinnedSearchTerm, setPinnedSearchTerm] = useState("");
    const [pinnedMessages, setPinnedMessages] = useState<any[]>([]); // This will be updated by the message tab

    // Get current user profile for pinned message management
    const [userProfile] = useGetUserProfile();
    const { fetchUserProfileSuccess } = useAppSelector((state) => state.auth);
    const currentUser = fetchUserProfileSuccess || userProfile;
    const currentUserId = currentUser?._id || currentUser?.ldbId || "unknown-user";

    // Handle pinned messages updates from the message tab
    const handlePinnedMessagesChange = useCallback((newPinnedMessages: any[]) => {
        setPinnedMessages(newPinnedMessages);
    }, []);

    const handleJumpToMessage = (messageId: string) => {
        // Switch to Messages tab if not already active
        if (activeTab !== 0) {
            setActiveTab(0);
        }
        // Close pinned sidebar
        setOpenPinnedSideBar(false);
        // The message jumping logic is handled by the message tab permalink feature
        navigate(`/spaces/${spaceId}/messages/${messageId}`);
    };

    const handleUnpinMessage = (messageId: string) => {
        // This will be handled by the message tab's unpin functionality
        // We just trigger the action and the message tab will handle the API call
        console.log("🎯 SpaceDetails: Triggering unpin for message:", messageId);
        // The actual implementation should use the custom hook from the message tab
        // For now, this is a placeholder that will be connected to the proper unpin logic
    };

    const isEmpty = !spaceId;
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();

    const getSpaceQuery = useGetSingleSpace(
        useMemo(() => ({ path: { spaceId } }), [spaceId]),
        { skip: !spaceId }
    );
    const spaceTabs = [
        {
            title: "Messages",
            content: (
                <SpaceDetailsMessageTab
                    spaceData={getSpaceQuery.data}
                    onPinnedMessagesChange={handlePinnedMessagesChange}
                />
            ),
            tooltip: "Chat with your team and Synth in real time.",
        },
        {
            title: "Products",
            content: <SpaceDetailsPinnedSkuTab />,
            tooltip: "Quick access to key products driving this Space.",
        },
        {
            title: "Forecast",
            content: <SpaceDetailsForecastTab />,
            tooltip: "See live demand and inventory projections.",
        },
        {
            title: "Tasks",
            content: <SpaceDetailsTaskTab spaceData={getSpaceQuery.data} />,
            tooltip: "Track work, assign owners, and monitor progress.",
        },
        {
            title: "Files",
            content: <SpaceDetailsFileTab spaceData={getSpaceQuery.data} />,
            tooltip: "Store and share related documents here.",
        },
        {
            title: "Notes",
            content: <SpaceDetailsNoteTab spaceData={getSpaceQuery.data} />,
            tooltip: "Capture meeting notes and ideas.",
        },
    ];

    return (
        <Paper
            className={clsx(
                expandConversation ? "w-[100%]" : "w-full",
                "transition-all duration-300 ease-in-out right-0 relative z-[1000] !p-0 overflow-hidden"
            )}
        >
            {isEmpty ? (
                <SpaceEmptyContent
                    icon="ri-question-answer-line"
                    title="No space selected yet"
                    description="Space details from a space will appear here once you select or create one."
                />
            ) : (
                <LoadingContent
                    loading={getSpaceQuery.isLoading}
                    error={getSpaceQuery.isError}
                    renderLoading={() => (
                        <div className="flex h-screen justify-center items-center">
                            <SpaceMessageLoadingState
                                isLoading={true}
                                message="Loading space..."
                                className="h-auto"
                            />
                        </div>
                    )}
                    renderError={() => (
                        <SpaceEmptyContent
                            icon="ri-error-warning-line"
                            title="Something went wrong"
                            description="Please try again later."
                            action={() => {
                                getSpaceQuery.fetchSingleSpace();
                            }}
                            actionText="Retry"
                        />
                    )}
                >
                    {() => (
                        <div className="flex h-full relative">
                            {/* Main content area - always full width */}
                            <div
                                className={clsx(
                                    "transition-all duration-300 ease-in-out",
                                    openPinnedSideBar || openMembersSidebar
                                        ? "w-[calc(100%-370px)]"
                                        : "w-full"
                                )}
                            >
                                <div className="flex flex-col h-full">
                                    <SpaceDetailsHeader
                                        spaceData={getSpaceQuery.data}
                                        expandConversation={expandConversation}
                                        handleToggleExpandConversation={
                                            handleToggleExpandConversation
                                        }
                                        isGuest={isGuest}
                                        activeTab={activeTab}
                                        togglePinnedSideBar={() => {
                                            setOpenMembersSidebar(false);
                                            togglePinnedSideBar();
                                        }}
                                        toggleMembersSidebar={() => {
                                            setOpenPinnedSideBar(false);
                                            toggleMembersSidebar();
                                        }}
                                    />

                                    <div className="mb-4">
                                        <TabsContainer
                                            tabs={spaceTabs.map((tab) => tab.title)}
                                            activeTab={activeTab}
                                            setActiveTab={(idx) => {
                                                setActiveTab(idx);

                                                setSearchParams({
                                                    tab: String(idx || 0),
                                                });
                                            }}
                                            tooltips={spaceTabs.map((tab) => tab.tooltip)}
                                            disableTabParam
                                            className="px-[24px] mt-[12px]"
                                            itemClassName="!pb-2"
                                            borderLineClase={"!text-slate-100 w-full px-[24px]"}
                                        />
                                        {getSpaceQuery.data?.isArchived && (
                                            <div className="flex items-center justify-center py-[8px] px-[24px] gap-2 bg-slate-100 bg-[#FFB9020D] border border-y-[#DDA00033] text-[#DDA000] p-4">
                                                <i className="ri-information-line text-lg"></i>
                                                <p className="text-sm font-rocGroteskMedium">
                                                    This space has been archived and is read-only.
                                                    To make changes or add content, please unarchive
                                                    it.
                                                </p>
                                            </div>
                                        )}
                                    </div>

                                    <div className="h-full overflow-y-scroll overflow-x-hidden scrollbar-hidden ">
                                        {spaceTabs?.[activeTab]?.content}
                                    </div>
                                </div>
                            </div>

                            {/* Sidebar overlay - positioned absolutely */}
                            <div className="absolute right-0 h-full">
                                <div
                                    className={clsx(
                                        "h-full transition-all duration-300 ease-in-out overflow-hidden border-l border-slate-100",
                                        openPinnedSideBar || openMembersSidebar
                                            ? "w-[370px]"
                                            : "w-0"
                                    )}
                                >
                                    {openPinnedSideBar && (
                                        <SpaceDetailsPinnedMessagesSidebar
                                            pinnedMessages={pinnedMessages}
                                            onJumpToMessage={handleJumpToMessage}
                                            onUnpinMessage={handleUnpinMessage}
                                            currentUserId={currentUserId}
                                        />
                                    )}
                                    {openMembersSidebar &&
                                        getSpaceQuery.data?.members?.length > 0 && (
                                            <SpaceDetailsMembersSidebar
                                                spaceData={getSpaceQuery.data}
                                            />
                                        )}
                                </div>
                            </div>
                        </div>
                    )}
                </LoadingContent>
            )}
        </Paper>
    );
}
